import type { ColumnDef } from '@tanstack/react-table';
import {
  createActionsColumn,
  createSerialColumn,
  MemberNameCell,
  StackedCellText,
  StackedListCell,
  WrappingCellText,
} from '@/components/common/DataTable';
import { ActiveStatusBadge } from '@/components/common/ActiveStatusBadge';
import { HighlightText } from '@/components/common/HighlightText';
import { RowEditDeleteButtons, RowViewLink } from '@/components/common/RowActions';
import { EMPTY_VALUE } from '@/constants/app.constants';
import { formatDisplayDate } from '@/utils/date';
import { formatNumber } from '@/utils/number';
import type {
  PromoAdjustmentUnit,
  PromoDistributionRow,
  PromoDrillParams,
  PromoInstitute,
  PromoItem,
  PromoItemType,
  PromoMember,
  UserPromo,
} from '../types/promotion.types';
import { PromoZmInstitutesLink } from './PromoZmInstitutesLink';

const dash = (value: string | null | undefined): string =>
  value === null || value === undefined || value === '' ? EMPTY_VALUE : value;

const qty = (value: number) => <span className="tabular-nums">{formatNumber(value)}</span>;

export interface RowActions<TRow> {
  onEdit: (row: TRow) => void;
  onDelete: (row: TRow) => void;
}

function actionsColumn<TRow extends { name: string }>(
  actions: RowActions<TRow>,
): ColumnDef<TRow, unknown> {
  return createActionsColumn<TRow>((item) => (
    <RowEditDeleteButtons
      item={item}
      itemName={item.name}
      onEdit={actions.onEdit}
      onDelete={actions.onDelete}
    />
  ));
}

export function getPromoAdjustmentColumns(
  zmSearch: PromoDrillParams,
  searchText?: string,
): ColumnDef<PromoDistributionRow, unknown>[] {
  return [
    createSerialColumn<PromoDistributionRow>(),
    {
      accessorKey: 'name',
      header: 'Promotional Item',
      cell: ({ row }) => (
        <WrappingCellText className="font-medium">
          <HighlightText text={row.original.name} highlightedText={searchText} />
        </WrappingCellText>
      ),
    },
    {
      accessorKey: 'itemNo',
      header: 'Item Id / Item Code',
      cell: ({ row }) => (
        <StackedCellText
          primary={row.original.itemNo}
          primaryClassName="text-xs tabular-nums"
          secondary={row.original.itemCode}
        />
      ),
    },
    {
      accessorKey: 'typeName',
      header: 'Type',
      cell: ({ row }) => dash(row.original.typeName),
    },
    {
      accessorKey: 'distributedQty',
      header: 'Distributed Qty',
      cell: ({ row }) => qty(row.original.distributedQty),
    },
    createActionsColumn<PromoDistributionRow>((item) => (
      <RowViewLink
        to="/promotion/adjustment/$promoItemId"
        params={{ promoItemId: item.promoItemId }}
        search={zmSearch}
        label="View ZMs"
        aria-label={`ZMs who handed out ${item.name}`}
      />
    )),
  ];
}

export function getPromoMemberColumns(
  unit: PromoAdjustmentUnit,
  promoItemId: number,
  zmSearch: PromoDrillParams,
): ColumnDef<PromoMember, unknown>[] {
  return [
    createSerialColumn<PromoMember>(),
    {
      accessorKey: 'name',
      header: 'ZM Name',
      cell: ({ row }) => (
        <MemberNameCell
          member={{
            name: row.original.name,
            hrEmpId: row.original.hrEmpId,
            mobile: row.original.userMobile,
          }}
        />
      ),
    },
    {
      accessorKey: 'designationName',
      header: 'Department, Designation',
      cell: ({ row }) => (
        <StackedCellText
          primary={dash(row.original.departmentName)}
          secondary={row.original.designationName}
        />
      ),
    },
    {
      accessorKey: 'thanaName',
      header: 'HR Division, Zilla, Thana',
      cell: ({ row }) => (
        <StackedCellText
          primary={dash(row.original.divisionName)}
          secondary={row.original.districtName}
          tertiary={row.original.thanaName}
        />
      ),
    },
    {
      accessorKey: 'distributedQty',
      header: 'Distributed',
      cell: ({ row }) => qty(row.original.distributedQty),
    },
    createActionsColumn<PromoMember>((member) => (
      <PromoZmInstitutesLink
        unit={unit}
        promoItemId={promoItemId}
        zm={member}
        zmSearch={zmSearch}
      />
    )),
  ];
}

export function getPromoInstituteColumns(): ColumnDef<PromoInstitute, unknown>[] {
  return [
    createSerialColumn<PromoInstitute>(),
    {
      accessorKey: 'instituteName',
      header: 'Institute',
      cell: ({ row }) => (
        <StackedCellText
          primary={<WrappingCellText>{dash(row.original.instituteName)}</WrappingCellText>}
          secondary={row.original.instituteCode}
        />
      ),
    },
    {
      accessorKey: 'instituteThana',
      header: 'Institute Division, Zilla, Thana',
      cell: ({ row }) => (
        <StackedCellText
          primary={dash(row.original.instituteDivision)}
          secondary={row.original.instituteDistrict}
          tertiary={row.original.instituteThana}
        />
      ),
    },
    {
      accessorKey: 'givenDate',
      header: 'Date',
      cell: ({ row }) => formatDisplayDate(row.original.givenDate),
    },
    {
      accessorKey: 'workTypeCode',
      header: 'Work',
      cell: ({ row }) => dash(row.original.workTypeCode),
    },
    {
      accessorKey: 'teacherMobiles',
      header: 'Received By',
      cell: ({ row }) => <StackedListCell listText={row.original.teacherMobiles} />,
    },
    {
      accessorKey: 'distributedQty',
      header: 'Qty',
      cell: ({ row }) => qty(row.original.distributedQty),
    },
  ];
}

export function getPromoItemColumns(
  searchText?: string,
  actions?: RowActions<PromoItem>,
): ColumnDef<PromoItem, unknown>[] {
  const columns: ColumnDef<PromoItem, unknown>[] = [
    createSerialColumn<PromoItem>(),
    {
      accessorKey: 'name',
      header: 'Promotion Name',
      cell: ({ row }) => (
        <WrappingCellText className="font-medium">
          <HighlightText text={row.original.name} highlightedText={searchText} />
        </WrappingCellText>
      ),
    },
    {
      accessorKey: 'legacyId',
      header: 'Item Id / Item Code',
      cell: ({ row }) => (
        <StackedCellText
          primary={row.original.legacyId ?? EMPTY_VALUE}
          primaryClassName="text-xs tabular-nums"
          secondary={row.original.code}
        />
      ),
    },
    { accessorKey: 'typeName', header: 'Type', cell: ({ row }) => dash(row.original.typeName) },
    {
      accessorKey: 'description',
      header: 'Item Description',
      cell: ({ row }) => <WrappingCellText>{dash(row.original.description)}</WrappingCellText>,
    },
    {
      accessorKey: 'bookEditionNo',
      header: 'Edition',
      cell: ({ row }) => row.original.bookEditionNo ?? '—',
    },
    {
      accessorKey: 'isActive',
      header: 'Active',
      cell: ({ row }) => <ActiveStatusBadge isActive={row.original.isActive} />,
    },
    {
      accessorKey: 'activeFrom',
      header: 'Active From',
      cell: ({ row }) => formatDisplayDate(row.original.activeFrom),
    },
    {
      accessorKey: 'activeTo',
      header: 'Active To',
      cell: ({ row }) => formatDisplayDate(row.original.activeTo),
    },
    { accessorKey: 'slNum', header: 'Sl num', cell: ({ row }) => row.original.slNum ?? '—' },
  ];

  if (actions) columns.push(actionsColumn(actions));
  return columns;
}

export function getPromoItemTypeColumns(
  searchText?: string,
  actions?: RowActions<PromoItemType>,
): ColumnDef<PromoItemType, unknown>[] {
  const columns: ColumnDef<PromoItemType, unknown>[] = [
    createSerialColumn<PromoItemType>(),
    {
      accessorKey: 'name',
      header: 'Type Name',
      cell: ({ row }) => (
        <WrappingCellText className="font-medium">
          <HighlightText text={row.original.name} highlightedText={searchText} />
        </WrappingCellText>
      ),
    },
    {
      accessorKey: 'code',
      header: 'Type Code',
      cell: ({ row }) => <span className="text-xs tabular-nums">{dash(row.original.code)}</span>,
    },
    {
      accessorKey: 'description',
      header: 'Type description',
      cell: ({ row }) => <WrappingCellText>{dash(row.original.description)}</WrappingCellText>,
    },
    {
      accessorKey: 'isActive',
      header: 'Active',
      cell: ({ row }) => <ActiveStatusBadge isActive={row.original.isActive} />,
    },
    {
      accessorKey: 'activeFrom',
      header: 'Active From',
      cell: ({ row }) => formatDisplayDate(row.original.activeFrom),
    },
    {
      accessorKey: 'activeTo',
      header: 'Active To',
      cell: ({ row }) => formatDisplayDate(row.original.activeTo),
    },
    {
      accessorKey: 'itemCount',
      header: 'Items',
      cell: ({ row }) => qty(row.original.itemCount),
    },
    { accessorKey: 'slNum', header: 'Sl Num', cell: ({ row }) => row.original.slNum ?? '—' },
  ];

  if (actions) columns.push(actionsColumn(actions));
  return columns;
}

export function getAssignmentColumns(): ColumnDef<UserPromo, unknown>[] {
  return [
    createSerialColumn<UserPromo>(),
    {
      accessorKey: 'name',
      header: 'User Name',
      cell: ({ row }) => (
        <WrappingCellText className="font-medium">{row.original.name}</WrappingCellText>
      ),
    },
    { accessorKey: 'hrEmpId', header: 'HR ID', cell: ({ row }) => dash(row.original.hrEmpId) },
    {
      accessorKey: 'assignDate',
      header: 'Assign Date',
      cell: ({ row }) => formatDisplayDate(row.original.assignDate),
    },
    {
      accessorKey: 'targetDateFrom',
      header: 'Target Date From',
      cell: ({ row }) => formatDisplayDate(row.original.targetDateFrom),
    },
    {
      accessorKey: 'targetDateTo',
      header: 'Target Date To',
      cell: ({ row }) => formatDisplayDate(row.original.targetDateTo),
    },
    {
      accessorKey: 'isActive',
      header: 'Active',
      cell: ({ row }) => <ActiveStatusBadge isActive={row.original.isActive} />,
    },
    { accessorKey: 'itemCount', header: 'Items', cell: ({ row }) => qty(row.original.itemCount) },
    { accessorKey: 'totalQty', header: 'Total Qty', cell: ({ row }) => qty(row.original.totalQty) },
  ];
}
